import isPropValid from "@emotion/is-prop-valid";
import styled from "@emotion/styled";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { ReactComponent as BinIcon } from "Components/_Shared/Icons/Assets/bin.svg";
import { ReactComponent as ChildIcon } from "Components/_Shared/Icons/Assets/child.svg";
import { ReactComponent as EditIcon } from "Components/_Shared/Icons/Assets/edit.svg";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { differenceInYears } from "date-fns";

type Props = {
  readonly onEdit: () => void;
  readonly onDelete?: () => void;
  readonly onSelect: (clientId: string) => void;
  readonly clientId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly selected: boolean;
  readonly disabled?: boolean;
  readonly birthDate?: Date;
};

const StyledWrapper = styled(Box, {
  shouldForwardProp: isPropValid,
})<{ $selected: boolean; $disabled?: boolean }>`
  ${({ theme, $selected, $disabled }) => `
    box-sizing: border-box;
    height: ${theme.spacing(16)};
    border: ${$selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.colors.borderLight}`};
    border-radius: ${theme.spacing(Radius.L)};
    padding: ${$selected ? theme.spacing(2.8) : theme.spacing(Spacing.L)};
    box-shadow: ${theme.shadows[1]};
    background: ${$disabled ? theme.colors.primaryDisabled : theme.colors.white};

    & > svg {
      color: ${theme.palette.text.primary}
    }

      &:hover {
    cursor: ${$disabled ? "default" : "pointer"};
  }
  `}
`;

export const ChildTile: React.FC<Props> = ({ clientId, selected, firstName, lastName, birthDate, disabled, onSelect, onDelete }) => {
  const { t } = useResource();

  const renderAge = (birthDate: Date) => {
    const age = differenceInYears(new Date(), birthDate);

    if (age === 1) {
      return `${age} ${t(Resources.Common.Year)}`;
    }

    if (age <= 4) {
      return `${age} ${t(Resources.Common.YearsFew)}`;
    }

    return `${age} ${t(Resources.Common.Years)}`;
  };

  return (
    <StyledWrapper $selected={selected} $disabled={disabled} onClick={() => (!disabled ? onSelect(clientId) : undefined)}>
      <Box height="100%" display="flex" justifyContent="space-between" alignItems="center">
        <Box height="100%" display="flex" alignItems="center">
          <ChildIcon />
          <Box display="flex" flexDirection="column" ml={Spacing.L}>
            <Typography variant="body2" fontWeight={600}>
              {firstName} {lastName}
            </Typography>
            <Typography variant="caption" color="secondary">
              {birthDate ? renderAge(birthDate) : t(Resources.AnalysisDetail.Goals.PlannedChild)}
            </Typography>
          </Box>
        </Box>
        {!disabled && onDelete && (
          <Box>
            {/* <IconButton onClick={onEdit}>
              <EditIcon color={theme.palette.primary.main} width={16} height={16} />
            </IconButton> */}
            <IconButton onClick={onDelete}>
              <BinIcon width={16} height={16} />
            </IconButton>
          </Box>
        )}
      </Box>
    </StyledWrapper>
  );
};
